import { useEffect, useRef, useState } from "react";
import { Award, Headphones, Users } from "lucide-react";
import Reveal from "./Reveal";

const ICONS = [Award, Headphones, Users];

function parseValue(value) {
  const match = String(value).match(/^(\D*)(\d+)(\D*)$/);
  if (!match) return null;
  return { prefix: match[1], num: parseInt(match[2], 10), suffix: match[3] };
}

function Counter({ value }) {
  const ref = useRef(null);
  const startedRef = useRef(false);
  const [display, setDisplay] = useState(0);
  const parsed = parseValue(value);

  useEffect(() => {
    if (!parsed || startedRef.current) return;
    const target = parsed.num;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          observer.disconnect();
          const duration = 1400;
          const start = performance.now();
          const step = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            setDisplay(Math.round((1 - Math.pow(1 - progress, 3)) * target));
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.4 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (parsed && startedRef.current) setDisplay(parsed.num);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!parsed) return <span ref={ref}>{value}</span>;
  return (
    <span ref={ref} data-testid="stat-counter">
      {parsed.prefix}
      {display}
      {parsed.suffix}
    </span>
  );
}

export default function Stats({ settings }) {
  const stats = settings?.statistics?.length
    ? settings.statistics
    : [
        { value: "6+", label: "Yıllık Deneyim" },
        { value: "7/24", label: "Aktif Destek" },
        { value: "100+", label: "Yayıncı" },
      ];

  return (
    <section data-testid="stats-section" className="relative py-20 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {stats.map((s, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <Reveal key={i} delay={i * 90}>
                <div
                  data-testid={`stat-card-${i}`}
                  className="card-lift relative overflow-hidden rounded-2xl border border-white/[0.06] bg-[#0A0E17] p-6 md:p-8"
                >
                  <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-[#0047FF]/15 blur-2xl" />
                  <Icon size={22} strokeWidth={1.5} className="text-cyan-400 mb-4" />
                  <div className="font-display font-700 text-2xl md:text-3xl text-white">
                    <Counter value={s.value} />
                  </div>
                  <div className="mt-2 text-sm text-[#A0ABC0]">{s.label}</div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
